/**
 * Cancel / re-run for a hosted external test run.
 *
 * Deliberately its own module rather than props on the native run mutations:
 * those act on `workflow_test_executions` and must never be handed an external
 * run id. Two hooks that cannot be confused beats one that takes a kind flag.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { ExternalAreaSettings, SuiteAlertConfig } from '../test-suites/types';

export interface CancelExternalRunResult {
  runId: string;
  status: 'canceled';
  /**
   * How many shards we ASKED to stop the container they were holding — not a
   * promise that any of them did. A container the substrate has lost track of
   * runs to completion regardless. Surface it as "stopping", never "stopped".
   */
  containersStopRequested: number;
  /** Shards canceled while still queued: they never started a container. */
  shardsNeverStarted: number;
  /**
   * Shards that were running and could not be reached. Their containers run
   * until they finish; their results are refused on arrival. Worth saying out
   * loud — this is the case where "canceled" does not mean "stopped".
   */
  shardsUnreachable: number;
}

/**
 * The one honest phrasing of a cancel, shared by both surfaces that offer one.
 *
 * A single boolean used to drive this, and it was derived from "the abort call
 * did not throw" — which was true for a run whose orchestrator had already
 * completed and whose containers were all still running, so the toast said
 * "stopping the containers" having stopped none. Each clause below now
 * corresponds to something the server actually established.
 */
export function describeExternalCancel(result: CancelExternalRunResult): string {
  const parts: string[] = [];
  if (result.containersStopRequested > 0) {
    parts.push(
      result.containersStopRequested === 1
        ? 'Canceling — asked 1 container to stop'
        : `Canceling — asked ${result.containersStopRequested} containers to stop`
    );
  } else if (result.shardsNeverStarted > 0) {
    parts.push('Run canceled before any container started');
  } else {
    parts.push('Run canceled');
  }
  if (result.shardsUnreachable > 0) {
    parts.push(
      result.shardsUnreachable === 1
        ? '1 container could not be reached and may run to completion'
        : `${result.shardsUnreachable} containers could not be reached and may run to completion`
    );
  }
  // Always last, because it is the one part that is unconditionally true.
  parts.push('No further tests will be scheduled or recorded.');
  return parts.join('. ');
}

export interface StartExternalRunResult {
  kind: 'external';
  jobId: string;
  runId: string;
  /** Already a full app path, e.g. `/runs/external/<runId>`. */
  runUrl: string;
  startedAt: string;
}

export function useCancelExternalRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string): Promise<CancelExternalRunResult> => {
      const res = await fetch(`${API_URL}/external-tests/runs/${runId}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to cancel this run');
      const body = (await res.json()) as { ok: boolean; data: CancelExternalRunResult };
      return body.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['external-run', data.runId] });
      void qc.invalidateQueries({ queryKey: ['workflow-test-executions'] });
    },
  });
}

/**
 * Enable/disable a directory area on the runner's config — not scoped to
 * this run. Takes effect from the NEXT run; the run currently on screen keeps
 * whatever its own area snapshot already says. `suiteId` is the test suite's
 * id (the runner config route lives at `/test-suites/:id/external-runner/…`),
 * distinct from `runnerId`.
 *
 * `runId` is optional: the run-detail page passes its own run's id so this
 * row's own label flips immediately; the suite config page (which has no run
 * in view) omits it and relies on the `test-suite`/`test-suites` invalidation
 * alone.
 */
export function useSetAreaDisabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      suiteId,
      path,
      disabled,
    }: {
      suiteId: string;
      runId?: string;
      path: string;
      disabled: boolean;
    }): Promise<{ disabledAreas: string[] }> => {
      const res = await fetch(`${API_URL}/test-suites/${suiteId}/external-runner/areas/disabled`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, disabled }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update this area');
      const body = (await res.json()) as { ok: boolean; data: { disabledAreas: string[] } };
      return { disabledAreas: body.data.disabledAreas };
    },
    onSuccess: (_data, variables) => {
      if (variables.runId) {
        void qc.invalidateQueries({ queryKey: ['external-run', variables.runId] });
      }
      void qc.invalidateQueries({ queryKey: ['test-suites'] });
      void qc.invalidateQueries({ queryKey: ['test-suite', variables.suiteId] });
    },
  });
}

/**
 * Save one area's own alert destination and/or triage switch (CAP-ETS-011).
 * `suiteId` is the test suite's id, `area` is the area's resolved path — the
 * same value the disable switch and the run/schedule area actions use.
 */
export function useSetAreaSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      suiteId,
      area,
      alertConfig,
      triageEnabled,
    }: {
      suiteId: string;
      area: string;
      alertConfig?: SuiteAlertConfig;
      triageEnabled?: boolean;
    }): Promise<{ path: string; settings: ExternalAreaSettings | null }> => {
      const res = await fetch(`${API_URL}/test-suites/${suiteId}/external-runner/areas/settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          area,
          ...(alertConfig !== undefined ? { alertConfig } : {}),
          ...(triageEnabled !== undefined ? { triageEnabled } : {}),
        }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save this area');
      const body = (await res.json()) as {
        ok: boolean;
        data: { path: string; settings: ExternalAreaSettings | null };
      };
      return body.data;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['test-suites'] });
      void qc.invalidateQueries({ queryKey: ['test-suite', variables.suiteId] });
    },
  });
}

/**
 * Start a FRESH run of the same runner.
 *
 * A new run row, never a mutation of the old one — the previous run's items and
 * verdicts are the record of what happened, and re-opening it would fold two
 * executions into one summary that describes neither.
 */
export function useRerunExternalRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      runId,
      areaIndex,
      failedOnly,
    }: {
      runId: string;
      /** Re-run ONE area of the run instead of the whole selection. */
      areaIndex?: number;
      /** Re-run only the tests that didn't cleanly pass (failed, flaky, skipped). */
      failedOnly?: true;
    }): Promise<StartExternalRunResult> => {
      const hasBody = areaIndex !== undefined || failedOnly !== undefined;
      const res = await fetch(`${API_URL}/external-tests/runs/${runId}/rerun`, {
        method: 'POST',
        credentials: 'include',
        headers: hasBody ? { 'Content-Type': 'application/json' } : undefined,
        body: hasBody ? JSON.stringify({ areaIndex, failedOnly }) : undefined,
      });
      if (!res.ok) throw await toApiError(res, 'Failed to re-run this suite');
      const body = (await res.json()) as { ok: boolean; data: StartExternalRunResult };
      return body.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workflow-test-executions'] });
      void qc.invalidateQueries({ queryKey: ['test-suites'] });
    },
  });
}
